import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { flow } from "lodash";
import moment from "moment-timezone";

import { Role } from "@teamrota/authlib";

import useAuth from "~/src/auth/hooks/use-auth";
import { CANCELLATION_REASONS } from "~/src/consts";
import Button from "~/src/components/button";
import { errorModal } from "~/src/utils/errors";
import getProfile from "~/src/graphql/queries/get-profile/get-profile-query.decorator";
import { getPolicyQueryParams } from "~/src/utils/formatting";
import asyncConfirm from "~/src/utils/async-confirm";
import updateShiftState from "~/src/graphql/mutations/update-shift-state/update-shift-state.decorator";
import Modal from "~/src/components/modal";
import stopAlgoDelay from "~/src/graphql/mutations/stop-algo-delay";
import createOrUpdateBonusReward from "~/src/graphql/mutations/bonus-rewards/create-or-update-bonus-reward";
import { showMemberDetails } from "~/src/containers/pools/reducer";
import getShiftGroup from "./graphql/get-shift-group/get-shift-group.query";
import ShiftDetails from "./components/shift-details";
import { ModalInner } from "./shift-modal.styles";
import EditShift from "./components/edit-shift";
import getAllRoles from "./graphql/get-all-roles";
import createOrUpdateShiftFn from "../graphql/create-or-update-shift";
import handleEditsWithinCancellationPolicyPeriod from "./handle-edits-within-cancellation-policy-period";
import { parseShiftToForm, parseToSavableData } from "./utils";

import {
  updateFormState,
  onModalClose,
  setFormState,
  onUpdateUniform
} from "./reducer";

import {
  createVenueOptions,
  createUniformOptions,
  createBriefingOptions,
  createRoleRateOptions,
  getIsPolicyApplied,
  getHours,
  getRoleRates
} from "../utils";

import { clearBonusState } from "../reducer";

const ShiftEditModal = ({
  policy,
  totalInGroup,
  shiftId,
  isLoadingShift,
  user,
  shift,
  warningFields,
  shiftForm,
  targetAccountId,
  bonuses,
  dispatch,
  onClose,
  createOrUpdateShift,
  updateShiftState,
  createOrUpdateBonusReward,
  stopAlgoDelay
}) => {
  const auth = useAuth();

  const [isEditMode, setIsEditMode] = useState(false);
  const [isSavingEdits, setIsSavingEdits] = useState(false);
  const [isSavedEdits, setIsSavedEdits] = useState(false);
  const [isCancellingShift, setIsCancellingShift] = useState(false);
  const [isOpeningShift, setIsOpeningShift] = useState(false);
  const [shiftOpened, setShiftOpened] = useState(false);

  useEffect(() => {
    dispatch(setFormState(parseShiftToForm({ user, shift })));
  }, [shift]);

  const addBonusToShift = async shift => {
    try {
      const shiftId = shift.shifts[0].id;
      await bonuses.forEach(bonus => {
        bonus.items.forEach(item => {
          if (item.amount && item.type)
            createOrUpdateBonusReward({
              shiftId,
              amount: Number(item.amount),
              period: item.type,
              type: "bonus"
            });
        });
      });
    } catch (e) {
      console.log("There was a problem adding the bonus/es to the shift: ", e);
    }
  };

  const handleClose = () => {
    onClose();
    dispatch(onModalClose());
    dispatch(clearBonusState());
    // HACK: Reset the state after its gone.
    setTimeout(() => {
      setIsEditMode(false);
      setShiftOpened(false);
    }, 0);
  };

  const handleSaveEdits = async () => {
    const [shift] = shiftForm?.shifts;
    const hours = getHours(shift);
    const minimumShiftLength = policy?.minimumShiftLength;
    const minimumShiftPercentage = policy?.minimumShiftPercentage;

    const originalStartTime = shift?.originalStartTime || shift?.startTime;
    const originalEndTime = shift?.originalEndTime || shift?.endTime;

    const startIsSame = moment(shift?.startTime).isSame(
      moment(originalStartTime)
    );
    const endIsSame = moment(shift?.endTime).isSame(moment(originalEndTime));

    const timeIsDiff = !(startIsSame && endIsSame);

    if (
      timeIsDiff &&
      moment().isAfter(shift?.originalStartTime || shift?.startTime)
    ) {
      return errorModal("Cannot edit shift time after it has started");
    }

    const errorMessage = handleEditsWithinCancellationPolicyPeriod(
      { ...shift, originalStartTime, originalEndTime },
      policy
    );

    if (errorMessage) {
      return errorModal(errorMessage);
    }

    const needsWarning =
      timeIsDiff ||
      shift?.briefing !== shift?.briefing ||
      shift?.venue?.id !== shift?.venueId ||
      (shiftForm?.uniformChanges || []).includes(shift?.id);

    const hasBeenWarned =
      !needsWarning ||
      (await asyncConfirm("Fulfilment alert", {
        subComponent: () => (
          <span>
            {`The change you're about to make to this shift may result in accepted members cancelling off of the shift. " + 
            "Are you sure you want to make this change?`}
          </span>
        ),
        confirmButtonText: "Proceed",
        falseButtonText: "Back"
      }));

    if (hasBeenWarned) {
      if (
        !getIsPolicyApplied(policy, shift) ||
        (await asyncConfirm("Are you sure you want to edit this shift?", {
          subComponent: () => (
            <span>
              The shift length is {hours} hours so a minimum of{" "}
              {minimumShiftLength} hours or {minimumShiftPercentage}% will be
              charged in accordance with our{" "}
              <a href={getPolicyQueryParams(policy)} target="_blank">
                Minimum Shift Length Policy.
              </a>
            </span>
          ),
          confirmButtonText: "Save",
          falseButtonText: "Back"
        }))
      ) {
        setIsSavingEdits(true);
        setIsSavedEdits(false);
        const savableShift = {
          shouldSkipTimeValidation: true,
          ...parseToSavableData(shiftForm, [])
        };

        try {
          await createOrUpdateShift(savableShift);

          if (auth.hasAnyRole(Role.SHIFTS_COST, Role.SHIFTS_PAY)) {
            await addBonusToShift(savableShift);
          }

          setIsSavingEdits(false);
          setIsSavedEdits(true);
          await Button.successDelay();
          handleClose();
        } catch (e) {
          errorModal(e);
          setIsSavingEdits(false);
          setIsSavedEdits(false);
        }
      }
    }

    return undefined;
  };

  const handleCancelShift = async () => {
    const hoursFromShift = moment(shift.startTime).diff(
      moment(),
      "hours",
      true
    );
    const period = policy?.cancellationPeriod;
    const length = policy?.minimumShiftLength;
    const percertage = policy?.minimumShiftPercentage;
    const isCancellationPolicyApplied = policy && hoursFromShift < period;
    if (totalInGroup > 1) {
      const confirmPopUp = await asyncConfirm(
        "Are you sure you want to cancel this shift?",
        {
          confirmButtonText: "Cancel individual shift",
          falseButtonText: "Back",
          cancelEntireLinkedShift: "Cancel future shifts",
          isLinkedShift: true,
          callback: () => {},
          dropDownOptions: Object.values(CANCELLATION_REASONS).map(reason => {
            return { value: reason, label: reason };
          }),
          subComponent: isCancellationPolicyApplied
            ? () => (
                <span>
                  Only future shifts in this linked shift will be cancelled. Any
                  shifts cancelled less than {period} hours before the shift
                  start and so a minimum of {length} hours or {percertage}% will
                  be charged in accordance with our{" "}
                  <a href={getPolicyQueryParams(policy)} target="_blank">
                    Minimum Shift Length Policy.
                  </a>
                </span>
              )
            : null,
          hasMarginBottom: true
        }
      );

      if (
        ["cancelledIndividualLinkedShift", "cancelledShift"].includes(
          confirmPopUp.type
        )
      ) {
        setIsCancellingShift(true);
        try {
          await updateShiftState(
            shift?.id,
            confirmPopUp.type === "cancelledIndividualLinkedShift"
              ? confirmPopUp.type
              : "cancelled",
            confirmPopUp.reason
          );
          setIsEditMode(false);
          setIsCancellingShift(false);
        } catch (e) {
          errorModal(e);
          setIsCancellingShift(false);
        }
      }
    } else {
      const ret = await asyncConfirm(
        "Are you sure you want to cancel this shift?",
        {
          confirmButtonText: "Cancel shift",
          falseButtonText: "Back",
          dropDownOptions: Object.values(CANCELLATION_REASONS).map(reason => {
            return { value: reason, label: reason };
          }),
          callback: () => {},
          subComponent: isCancellationPolicyApplied
            ? () => (
                <span>
                  The shift is being cancelled less than {period} hours before
                  the shift start and so a minimum of {length} hours or{" "}
                  {percertage}% will be charged in accordance with our{" "}
                  <a href={getPolicyQueryParams(policy)} target="_blank">
                    Minimum Shift Length Policy.
                  </a>
                </span>
              )
            : null,
          hasMarginBottom: true
        }
      );
      if (ret) {
        setIsCancellingShift(true);
        try {
          await updateShiftState(shift?.id, "cancelled", ret);
          setIsEditMode(false);
          setIsCancellingShift(false);
        } catch (e) {
          errorModal(e);
          setIsCancellingShift(false);
        }
      }
    }
  };

  const handleDeleteDraftShift = async () => {
    const ret = await asyncConfirm(
      "Are you sure you want to delete this draft?",
      {
        confirmButtonText: "Delete draft",
        falseButtonText: "Back",
        callback: () => {},
        hasMarginBottom: true
      }
    );
    if (ret) {
      setIsCancellingShift(true);
      try {
        await updateShiftState(shift?.id, "draftDeleted");
        setIsCancellingShift(false);
        handleClose();
        setIsEditMode(false);
      } catch (e) {
        errorModal(e);
        setIsCancellingShift(false);
      }
    }
  };

  const handleOpenShift = async () => {
    if (
      await asyncConfirm("Are you sure you want to open this shift?", {
        confirmButtonText: "Open shift",
        falseButtonText: "Back",
        isConfirmButtonGreen: true,
        hasMarginBottom: true
      })
    ) {
      setIsOpeningShift(true);
      try {
        await stopAlgoDelay(shift?.id);
        setIsOpeningShift(false);
        setShiftOpened(true);
      } catch (e) {
        errorModal(e);
        setIsOpeningShift(false);
      }
    }
  };

  if (!shiftId) return false;

  const roleRates = getRoleRates(user?.account?.roleRates || [], shift || {});

  const roleRateOptions = createRoleRateOptions({
    roleRates: user.account.roleRates || [],
    accountId: "",
    shift: shift || {}
  });

  const briefingOptions = createBriefingOptions(
    user?.account?.briefingTemplates || []
  );

  const venues =
    user?.venueIds?.length > 0
      ? user?.account?.venues.filter(({ id }) =>
          user?.venueIds.includes(parseInt(id))
        )
      : user?.account?.venues;

  const venueOptions = createVenueOptions(venues || []);

  const uniformOptions = createUniformOptions(
    user?.account?.uniformTemplates || [],
    true
  );

  const alreadyExistingBonuses = shift?.bonuses
    ? shift.bonuses.filter(bonus => bonus?.type === "bonus")
    : [];

  const onEdit = () => setIsEditMode(true);

  const onBack = () => setIsEditMode(false);

  const showMember = id => dispatch(showMemberDetails(id));

  const onFormUpdate = shiftFormState =>
    dispatch(updateFormState(shiftFormState));

  return (
    <Modal
      onClose={handleClose}
      isLoading={Boolean(isLoadingShift)}
      isOpen={Boolean(shiftId)}
    >
      <ModalInner>
        <ShiftDetails
          isVisible={!isEditMode}
          shift={shift}
          onEdit={onEdit}
          showMember={showMember}
        />
        <EditShift
          isVisible={isEditMode}
          policy={shift?.policy}
          isShiftCancelled={shift.cancelledAt}
          warningFields={warningFields || []}
          shiftFormState={shiftForm}
          onBack={onBack}
          roleRates={roleRates}
          roleRateOptions={roleRateOptions}
          venueOptions={venueOptions}
          briefingOptions={briefingOptions}
          uniformOptions={uniformOptions}
          initialShift={shift}
          isSaving={isSavingEdits}
          isSaved={isSavedEdits}
          isCancellingShift={isCancellingShift}
          isOpeningShift={isOpeningShift}
          onCancelShift={handleCancelShift}
          onOpenShift={handleOpenShift}
          onSave={handleSaveEdits}
          onFormUpdate={onFormUpdate}
          shiftOpened={shiftOpened}
          targetAccountId={targetAccountId}
          onUpdateUniform={id => dispatch(onUpdateUniform(id))}
          alreadyExistingBonuses={alreadyExistingBonuses}
          accountId={user?.account?.id}
          isAccountRebookingEnabled={user?.account?.isRebookingEnabled}
          onDeleteDraftShift={handleDeleteDraftShift}
        />
      </ModalInner>
    </Modal>
  );
};

const mapStateToProps = state => ({
  shiftForm: state?.shiftModal?.formState,
  warningFields: state?.shiftModal?.warningFields,
  bonuses: state?.calendar?.bonuses,
  selectedMemberId: state?.pools?.selectedMemberId
});

export default flow(
  connect(mapStateToProps),
  createOrUpdateBonusReward,
  getShiftGroup,
  getProfile,
  createOrUpdateShiftFn,
  updateShiftState,
  stopAlgoDelay,
  getAllRoles
)(ShiftEditModal);
